import React, {useState, useEffect} from "react"
import {isValidPhoneNumber, parsePhoneNumber} from "react-phone-number-input"

import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import {PhoneInput, ValidationButton} from "@irati/ui/src/FormItem"
import Form from "@irati/ui/src/Form"
import Validation from "@irati/utils/validation"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import getCurrentLocale from "@irati/utils/lang/helpers/getCurrentLocale"
import tracking from "@irati-tracking/platform"
import {LoginType, LoginProduct} from "@irati-tracking/platform/platform"

interface Props {
  load: Promise<any> | null
  show: boolean
  error: string
  phoneNo?: string
  onHide: () => void
  onNext: (phone: string) => void
}

const EnterPhoneNoModal: React.FC<Props> = ({show, load, error, phoneNo, onHide, onNext}) => {
  const convertZeroToCountryCode = (phoneNumber: string) => {
    if (phoneNumber[0] === "0") {
      const locale = getCurrentLocale()
      const phoneNumberWithoutLeadingZero = phoneNumber.substring(1)
      switch (locale) {
        case "fr-FR":
          return `+33${phoneNumberWithoutLeadingZero}`
        case "en-AU":
          return `61${phoneNumberWithoutLeadingZero}`
        default:
          return `+44${phoneNumberWithoutLeadingZero}`
      }
    }
    return phoneNumber
  }

  const [showModal, setShowModal] = useState<boolean>(show)
  const [phone, setPhone] = useState(phoneNo ? convertZeroToCountryCode(phoneNo) : "")
  const [phoneError, setPhoneError] = useState<string>(error || "")

  const {formatMessage} = useIntl()

  const handleNext = () => {
    if (phone && isValidPhoneNumber(phone)) {
      const parsedPhoneNumber = parsePhoneNumber(phone)?.number || phone
      onNext(parsedPhoneNumber)
    }
  }

  const handleOnInputChange = (value) => {
    setPhoneError("")
    setPhone(value)
  }

  const handleOnHide = () => {
    setPhoneError("")
    onHide()
  }

  useEffect(() => {
    if (show) {
      tracking.phoneCaptureViewPage("phone_number_capture", LoginType.phoneCapture, LoginProduct.myAccount)
      tracking.loginViewPage("phone_number_capture", LoginType.phoneCapture, LoginProduct.myAccount)
    }
    setShowModal(show)
    setPhone(phoneNo ? convertZeroToCountryCode(phoneNo) : "")
  }, [show])

  useEffect(() => {
    setPhoneError(error)
  }, [error])

  useEffect(() => {
    setPhone(convertZeroToCountryCode(phoneNo))
  }, [phoneNo])

  return (
    <GenericModal customWidth={500} hideActions show={showModal} onHide={handleOnHide}>
      {!!phoneError && (
        <Alert
          type={AlertTypes.error}
          title={phoneError}
          subTitle="platform.oneTimePassword.enterEmail.error.text"
          onHide={() => setPhoneError("")}
        />
      )}
      <Body marginTop={2} marginBottom textAlign="center" weight="bold" maxWidth="100%">
        <FormattedMessage id="account.profile.verifyPhoneNo.title" />
      </Body>
      <Body marginBottom={3} textAlign="center" maxWidth="100%">
        <FormattedMessage id="account.profile.verifyPhoneNo.subTitle" />
      </Body>
      <Flex direction="column" width="100%">
        <Form>
          <PhoneInput
            value={phone}
            validation={Validation.string(formatMessage({id: "platform.mfa.phoneNumber.errors.isRequired"}))}
            label={formatMessage({id: "account.profile.verifyPhoneNo.inputLabel"})}
            placeholder={formatMessage({id: "account.profile.verifyPhoneNo.inputPlaceholder"})}
            onChange={handleOnInputChange}
          />
          <ValidationButton load={load} mobileFull full primary onClick={handleNext}>
            <FormattedMessage id="account.profile.verifyPhoneNo.nextBtn" />
          </ValidationButton>
        </Form>
      </Flex>
    </GenericModal>
  )
}

export default EnterPhoneNoModal
